import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";

interface QueryParams {
  query: string;
  page: number;
}

export const useQueryParams = (customQueryName: string = "q") => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const [params, setParams] = useState<QueryParams>(() => ({
    query: searchParams.get(customQueryName) || "",
    page: Number(searchParams.get("p")) || 1,
  }));

  const debouncedUpdateQuery = debounce((value: string) => {
    const newParams = new URLSearchParams(searchParamsRef.current);
    if (value) {
      newParams.set(customQueryName, value);
    } else {
      newParams.delete(customQueryName);
    }
    setSearchParams(newParams, { replace: true });
  }, 500);

  // Update URL when 'page' changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (params.page && params.page !== 1) {
      newParams.set("p", params.page.toString());
    } else {
      newParams.delete("p");
    }
    setSearchParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page]);

  // Update URL when 'query' changes (debounced)
  useEffect(() => {
    debouncedUpdateQuery(params.query);
    return () => {
      debouncedUpdateQuery.cancel();
    };
  }, [params.query]);

  // Sync state with URL if user navigates (back/forward)
  useEffect(() => {
    const urlQuery = searchParams.get(customQueryName) || "";
    const urlPage = Number(searchParams.get("p")) || 1;
    setParams((prev) => {
      if (prev.query !== urlQuery || prev.page !== urlPage) {
        return { ...prev, query: urlQuery, page: urlPage };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, customQueryName]);

  const updateParams = (newParams: Partial<QueryParams>) => {
    setParams((prev) => ({ ...prev, ...newParams }));
  };

  return {
    params,
    updateParams,
  };
};
